import { useEffect } from "react";
import { replaceRobotsMeta } from "./robots";

export const HOST = "https://secondlifeappliances.com";

export function altLinks(path, urls) {
  const normalized = path?.startsWith("/") ? path : `/${path || ""}`;
  if (urls?.en && urls?.es) {
    return {
      canonical: normalized,
      en: urls.en,
      es: urls.es,
      "x-default": urls.en,
    };
  }
  const localized = normalized.match(/^\/(en|es)(\/.*)?$/);
  if (!localized) {
    return { canonical: null, en: null, es: null, "x-default": null };
  }
  const suffix = localized[2] || "/";
  return {
    canonical: normalized,
    en: `/en${suffix}`,
    es: `/es${suffix}`,
    "x-default": `/en${suffix}`,
  };
}

// Manages <head> for indexable store pages: title, description, canonical, hreflang (en/es/x-default), robots, JSON-LD.
export function useSeoHead({ title, description, urls, lang, robots = "index, follow", jsonLd = [], canonical, ogType = "website", ogImage }) {
  useEffect(() => {
    if (!title) return;
    document.head
      .querySelectorAll('[data-seo="1"], link[rel="canonical"], link[rel="alternate"]')
      .forEach((el) => el.remove());
    document.title = title;
    if (lang) document.documentElement.lang = lang;
    const made = [];
    const restored = [];
    const add = (tag, attrs, text) => { const el = document.createElement(tag); Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v)); if (text) el.textContent = text; document.head.appendChild(el); made.push(el); };
    // update the static index.html meta in place (restored on unmount) instead of adding a duplicate
    const meta = (key, name, content) => {
      const base = document.head.querySelector(`meta[${key}="${name}"]:not([data-seo])`);
      if (!base) return add("meta", { [key]: name, content, "data-seo": "1" });
      restored.push([base, base.getAttribute("content")]);
      base.setAttribute("content", content);
    };
    meta("name", "description", (description || "").slice(0, 160));
    replaceRobotsMeta(robots);
    const links = altLinks(urls?.[lang], urls);
    if (links.canonical) {
      add("link", { rel: "canonical", href: HOST + links.canonical });
      ["en", "es", "x-default"].forEach((locale) => {
        add("link", { rel: "alternate", hreflang: locale, href: HOST + links[locale] });
      });
      meta("property", "og:url", HOST + links.canonical);
    } else if (canonical) {
      add("link", { rel: "canonical", href: HOST + canonical });
      meta("property", "og:url", HOST + canonical);
    }
    meta("property", "og:type", ogType);
    meta("property", "og:title", title);
    meta("property", "og:description", (description || "").slice(0, 200));
    if (ogImage) meta("property", "og:image", ogImage);
    jsonLd.forEach((obj) => add("script", { type: "application/ld+json" }, JSON.stringify(obj)));
    return () => {
      made.forEach((el) => el.remove());
      restored.forEach(([el, content]) => el.setAttribute("content", content));
    };
  }, [title, description, lang, JSON.stringify(urls), robots, JSON.stringify(jsonLd), canonical, ogType, ogImage]); // eslint-disable-line react-hooks/exhaustive-deps
}

export const ORG = { "@context": "https://schema.org", "@type": "Organization", name: "Second Life Appliance Parts", url: HOST, logo: HOST + "/sl-logo.png", parentOrganization: { "@type": "Organization", name: "OWLICO Appliances" }, areaServed: "US" };

export const breadcrumbLd = (items) => ({ "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: HOST + it.url })) });

export const T = (lang, en, es) => (lang === "es" ? es : en);
export const COND = { used: ["Used · good condition", "Usado · buen estado"], "used-good": ["Used · good condition", "Usado · buen estado"], refurbished: ["Refurbished", "Reacondicionado"], new_open_box: ["New (open box)", "Nuevo (caja abierta)"] };
export const condLabel = (c, lang) => (COND[c] ? COND[c][lang === "es" ? 1 : 0] : c || "—");
export const AVAIL = { available: ["In stock", "Disponible"], reserved: ["Reserved", "Reservada"], sold: ["Sold", "Vendida"] };
export const availLabel = (a, lang) => (AVAIL[a] ? AVAIL[a][lang === "es" ? 1 : 0] : a);
